import { useEffect, useState, useCallback } from "react";

export default function Home() {
  const phone = "+40775649806";
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Lightbox state
  const [lightboxSrc, setLightboxSrc] = useState(null);

  // Back-to-top visibility
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 200);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Hardcoded gallery from /public
  const gallery = [
    "/clean/IMG-20251010-WA0001.jpg",
    "/clean/IMG-20251010-WA0002.jpg",
    "/clean/IMG-20251010-WA0003.jpg",
    "/clean/IMG-20251010-WA0004.jpg",
    "/clean/IMG-20251010-WA0005.jpg",
    "/clean/IMG-20251010-WA0006.jpg",
    "/clean/IMG-20251010-WA0007.jpg",
    "/clean/IMG-20251010-WA0008.jpg",
    "/clean/IMG-20251010-WA0009.jpg",
    "/clean/IMG-20251010-WA0010.jpg",
    "/clean/IMG-20251010-WA0011.jpg",
    "/clean/IMG-20251010-WA0012.jpg",
    "/clean/IMG-20251010-WA0013.jpg",
    "/clean/IMG-20251010-WA0014.jpg",
    "/clean/IMG-20251010-WA0015.jpg",
    "/clean/IMG-20251010-WA0016.jpg",
  ];

  // Lightbox: store index instead of src
  const [lightboxIndex, setLightboxIndex] = useState(null); // number|null

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const gotoPrev = useCallback(
    () =>
      setLightboxIndex((i) =>
        i === null ? i : (i + gallery.length - 1) % gallery.length
      ),
    [gallery.length]
  );
  const gotoNext = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i + 1) % gallery.length)),
    [gallery.length]
  );

  // Close lightbox helpers
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") gotoPrev();
      if (e.key === "ArrowRight") gotoNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, closeLightbox, gotoPrev, gotoNext]);

  return (
    <section id="home" className="section">
      {/* HERO */}
      <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
        <div className="space-y-6">
          <h1 className="h1">Curățenie în profunzime, direct la tine acasă!</h1>
          <p className="text-lg leading-relaxed text-slate-700">
            La <strong>Axor Clean Services</strong>, ne dedicăm complet
            curățeniei profesionale a tapițeriei auto, canapelelor, saltelelor
            și mochetelor – chiar în confortul casei tale. Suntem o echipă
            pasionată de rezultate impecabile, care folosește echipamente
            moderne și soluții profesionale pentru a reda prospețimea și igiena
            spațiilor tale preferate.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href={`tel:${phone}`}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-5 py-3 font-medium shadow-lg hover:opacity-90 transition"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M6.6 10.8c1.2 2.3 3.1 4.2 5.4 5.4l1.8-1.8c.3-.3.8-.4 1.2-.3 1 .3 2.1.5 3.2.5.7 0 1.2.5 1.2 1.2v3.1c0 .7-.5 1.2-1.2 1.2C9.7 21.3 2.7 14.3 2.7 5.8c0-.7.5-1.2 1.2-1.2H7c.7 0 1.2.5 1.2 1.2 0 1.1.2 2.2.5 3.2.1.4 0 .9-.3 1.2L6.6 10.8z"
                />
              </svg>
              Sună acum
            </a>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full border border-primary text-primary px-5 py-3 font-medium hover:bg-primary/10 transition"
            >
              Vezi contact
            </a>
          </div>
        </div>

        {/* Galerie MĂRITĂ + Lightbox */}
        <div className="relative">
          <div className="absolute -top-6 -left-6 h-24 w-24 rounded-full bg-primary/20 blur-2xl animate-float" />
          <div className="card-glass p-4">
            <div className="grid grid-cols-3 gap-4 md:gap-5 gallery">
              {["/clean/1.jpeg", "/clean/2.jpeg", "/clean/3.jpeg"].map(
                (src, i) => (
                  <button
                    key={src}
                    type="button"
                    onClick={() => setLightboxSrc(src)}
                    className="group relative block focus:outline-none rounded-xl overflow-hidden"
                    aria-label={`Mărește imaginea ${i + 1}`}
                    title="Click pentru mărire"
                  >
                    <img
                      src={src}
                      alt={`Curățare profesională ${i + 1}`}
                      loading="lazy"
                      className="
                      w-full 
                      h-36 sm:h-48 md:h-64 lg:h-72 
                      object-cover rounded-xl 
                      shadow-md group-hover:shadow-lg transition
                      cursor-zoom-in
                    "
                    />
                    {/* mic efect de overlay la hover */}
                    <span className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/10 transition" />
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* SERVICII + ICONURI */}
      <div className="max-w-6xl mx-auto px-4 mt-20">
        <h2 className="h2 mb-6">Serviciile noastre principale</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {/* Auto */}
          <article className="card-glass p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-primary">01.</div>
              <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center">
                {/* Car icon */}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M5 11l1.2-3.1A3 3 0 0 1 8.98 6h6.04a3 3 0 0 1 2.78 1.9L19 11v5a1 1 0 0 1-1 1h-1a2 2 0 1 1 0-4h2v-1H5v1h2a2 2 0 1 1 0 4H6a1 1 0 0 1-1-1v-5Zm2.35-2.6A1 1 0 0 1 8.98 8h6.04a1 1 0 0 1 .93.6L17.5 11h-11l.85-2.6Z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="h3 mb-2">Curățare tapițerie auto</h3>
            <p className="text-slate-700">
              Curățare profesională a tapițeriei auto, cu echipamente
              performante și soluții dedicate, pentru un interior curat,
              igienizat și fără mirosuri neplăcute.
            </p>
          </article>

          {/* Canapele / Fotolii */}
          <article className="card-glass p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-primary">02.</div>
              <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center">
                {/* Sofa icon */}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M5 10a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v1h1a1 1 0 0 1 1 1v3a2 2 0 0 1-2 2h-1v2h-2v-2H8v2H6v-2H5a2 2 0 0 1-2-2v-3a1 1 0 0 1 1-1h1v-1Zm2 0v1h10v-1a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1Z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="h3 mb-2">Curățare canapele, fotolii, etc.</h3>
            <p className="text-slate-700">
              Îți curățăm canapeaua în profunzime, direct la domiciliu! Eliminăm
              petele, praful și bacteriile, redând prospețimea țesăturii.
            </p>
          </article>

          {/* Mochete */}
          <article className="card-glass p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-primary">03.</div>
              <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center">
                {/* Carpet / layers icon */}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M12 3l9 5-9 5-9-5 9-5Zm-7.8 9.6L12 17l7.8-4.4L21 14l-9 5-9-5 1.2-1.4Z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="h3 mb-2">Curățare mochete</h3>
            <p className="text-slate-700">
              Mochete curate, culori vii, aer proaspăt! Cu Axor Clean Services,
              casa ta capătă un nou nivel de confort și igienă.
            </p>
          </article>
        </div>
      </div>

      {/* HIGHLIGHTS Auto/Casă/Mochete */}
      <div className="max-w-6xl mx-auto px-4 mt-20 grid md:grid-cols-3 gap-6">
        {/* Auto */}
        <div className="card-glass p-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center">
              {/* Car icon */}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M5 11l1.2-3.1A3 3 0 0 1 8.98 6h6.04a3 3 0 0 1 2.78 1.9L19 11v5a1 1 0 0 1-1 1h-1a2 2 0 1 1 0-4h2v-1H5v1h2a2 2 0 1 1 0 4H6a1 1 0 0 1-1-1v-5Zm2.35-2.6A1 1 0 0 1 8.98 8h6.04a1 1 0 0 1 .93.6L17.5 11h-11l.85-2.6Z"
                />
              </svg>
            </span>
            <h3 className="h3 m-0">Auto</h3>
          </div>
          <p className="text-slate-700">
            Igienă completă pentru mașina ta. Servicii mobile de top în
            Timișoara și împrejurimi.
          </p>
        </div>

        {/* Casă */}
        <div className="card-glass p-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center">
              {/* Home icon */}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M12 3l9 8h-2v9a1 1 0 0 1-1 1h-4v-6H10v6H6a1 1 0 0 1-1-1v-9H3l9-8Z"
                />
              </svg>
            </span>
            <h3 className="h3 m-0">Casă</h3>
          </div>
          <p className="text-slate-700">
            Petele dispar, prospețimea revine. Curățare tapițerie premium cu
            reduceri în funcție de cantitate.
          </p>
        </div>

        {/* Mochete */}
        <div className="card-glass p-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center">
              {/* Layers / carpet icon */}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M12 3l9 5-9 5-9-5 9-5Zm-7.8 9.6L12 17l7.8-4.4L21 14l-9 5-9-5 1.2-1.4Z"
                />
              </svg>
            </span>
            <h3 className="h3 m-0">Mochete</h3>
          </div>
          <p className="text-slate-700">
            Mochete impecabile, culori revigorate. Curățare profesională la tine
            acasă.
          </p>
        </div>
      </div>

      {/* PROCESUL + ICONURI */}
      <div className="max-w-6xl mx-auto px-4 mt-20">
        <h2 className="h2 mb-6">
          Cum transformăm curățenia într-o experiență fără griji
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {/* Pas 1 */}
          <div className="card-glass p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-primary">Pas 1</div>
              <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center">
                {/* Chat/checklist icon */}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H8l-4 3v-3H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm3 4h10v2H7V8Zm0 4h6v2H7v-2Z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="h3 mb-2">Exact ce ai nevoie</h3>
            <p className="text-slate-700">
              Ne contactezi, ne spui ce ai nevoie, iar noi îți oferim o soluție
              clară și adaptată exact cerințelor tale.
            </p>
          </div>

          {/* Pas 2 */}
          <div className="card-glass p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-primary">Pas 2</div>
              <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center">
                {/* Calendar icon */}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M7 2h2v2h6V2h2v2h3a2 2 0 0 1 2 2v3H2V6a2 2 0 0 1 2-2h3V2Zm15 7v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9h20ZM6 12h4v4H6v-4Zm6 0h4v4h-4v-4Z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="h3 mb-2">Stabilim fără bătăi de cap</h3>
            <p className="text-slate-700">
              Stabilim împreună ziua și ora care ți se potrivesc cel mai bine,
              pentru ca intervenția să fie comodă.
            </p>
          </div>

          {/* Pas 3 */}
          <div className="card-glass p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-primary">Pas 3</div>
              <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center">
                {/* Sparkles/clean icon */}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M12 2l1.8 4.2L18 8l-4.2 1.8L12 14l-1.8-4.2L6 8l4.2-1.8L12 2Zm7 9l1.2 2.8L23 15l-2.8 1.2L19 19l-1.2-2.8L15 15l2.8-1.2L19 11ZM4 13l1.2 2.8L8 17l-2.8 1.2L4 21l-1.2-2.8L0 17l2.8-1.2L4 13Z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="h3 mb-2">Curățare profesională</h3>
            <p className="text-slate-700">
              Echipa noastră lucrează cu atenție și echipamente performante; la
              final verificăm împreună rezultatul.
            </p>
          </div>
        </div>
      </div>

      {/* ÎNAINTE & DUPĂ – din /public */}
      <div id="inainte-dupa" className="max-w-6xl mx-auto px-4 mt-20">
        <h2 className="h2 text-center mb-2">Înainte & După</h2>
        <p className="text-center text-slate-600 mb-8">
          Rezultate reale, din lucrări făcute de noi.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {gallery.map((src, i) => (
            <button
              key={src}
              type="button"
              onClick={() => openLightbox(i)}
              className="group relative block focus:outline-none rounded-xl overflow-hidden"
              aria-label={`Mărește imaginea Înainte & După ${i + 1}`}
              title="Click pentru mărire"
            >
              <img
                src={src}
                alt={`Înainte & După ${i + 1}`}
                loading="lazy"
                className="w-full aspect-square object-cover rounded-xl shadow-md group-hover:shadow-lg transition cursor-zoom-in"
              />
              <span className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/10 transition" />
            </button>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-6xl mx-auto px-4 mt-20">
        <div className="card-glass p-8 md:p-10 flex flex-col md:flex-row md:flex-nowrap items-center justify-between gap-6">
          <div className="w-full">
            <h2 className="h2">Nu amâna!</h2>
            <p className="text-slate-700 mt-2">
              Programează-ți acum curățarea profesională și redă prospețimea
              casei sau mașinii tale. Confort și igienă, direct la tine acasă.
              Sună azi.
            </p>
          </div>

          <a
            href={`tel:${phone}`}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-6 py-3 font-semibold shadow-lg hover:opacity-90 transition whitespace-nowrap shrink-0 text-sm md:text-base"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M6.6 10.8c1.2 2.3 3.1 4.2 5.4 5.4l1.8-1.8c.3-.3.8-.4 1.2-.3 1 .3 2.1.5 3.2.5.7 0 1.2.5 1.2 1.2v3.1c0 .7-.5 1.2-1.2 1.2C9.7 21.3 2.7 14.3 2.7 5.8c0-.7.5-1.2 1.2-1.2H7c.7 0 1.2.5 1.2 1.2 0 1.1.2 2.2.5 3.2.1.4 0 .9-.3 1.2L6.6 10.8z"
              />
            </svg>
            <span>Sună acum</span>
          </a>
        </div>
      </div>

      {/* BUTOANE PLUTITOARE: Apel & Back-to-top */}
      <div className="fab">
        {/* Telefon: vizibil permanent */}
        <a
          href={`tel:${phone}`}
          aria-label="Sună Axorclean"
          className="fab-btn"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M6.6 10.8c1.2 2.3 3.1 4.2 5.4 5.4l1.8-1.8c.3-.3.8-.4 1.2-.3 1 .3 2.1.5 3.2.5.7 0 1.2.5 1.2 1.2v3.1c0 .7-.5 1.2-1.2 1.2C9.7 21.3 2.7 14.3 2.7 5.8c0-.7.5-1.2 1.2-1.2H7c.7 0 1.2.5 1.2 1.2 0 1.1.2 2.2.5 3.2.1.4 0 .9-.3 1.2L6.6 10.8z"
            />
          </svg>
        </a>

        {/* Back to top: apare doar după scroll */}
        {showBackToTop && (
          <button
            aria-label="Înapoi sus"
            className="fab-btn"
            onClick={scrollTop}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M12 5l7 7-1.4 1.4L13 9.8V20h-2V9.8l-4.6 3.6L5 12z"
              />
            </svg>
          </button>
        )}
      </div>

      {/* LIGHTBOX MODAL */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 md:p-6 pt-8 md:pt-10"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Vizualizare imagine mărită"
        >
          <div
            className="relative max-w-5xl w-full max-h-[88vh] flex items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* imaginea: puțin mai înaltă dar tot nu depășește viewport-ul */}
            <img
              src={gallery[lightboxIndex]}
              alt={`Imagine ${lightboxIndex + 1} din ${gallery.length}`}
              className="block mx-auto w-auto max-w-full max-h-[84vh] md:max-h-[86vh] object-contain rounded-2xl shadow-2xl select-none"
              draggable={false}
            />

            {/* Close – pe colțul imaginii, ușor în exterior ca înainte */}
            <button
              onClick={closeLightbox}
              aria-label="Închide"
              className="absolute -top-3 -right-3 h-10 w-10 rounded-full bg-white text-slate-900 shadow-lg grid place-items-center hover:scale-105 transition"
              style={{ zIndex: 5 }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M6 6l12 12M6 18L18 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            {/* Prev / Next */}
            <button
              onClick={gotoPrev}
              aria-label="Imaginea anterioară"
              className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 text-slate-900 shadow-lg grid place-items-center hover:bg-white transition"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M15.5 5l-7 7 7 7 1.5-1.5L11 12l6-6z"
                />
              </svg>
            </button>

            <button
              onClick={gotoNext}
              aria-label="Imaginea următoare"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 text-slate-900 shadow-lg grid place-items-center hover:bg-white transition"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M8.5 19l7-7-7-7-1.5 1.5L13 12l-6 6z"
                />
              </svg>
            </button>

            {/* Contor */}
            <div className="absolute bottom-2 right-4 text-xs md:text-sm px-2 py-1 rounded bg-black/60 text-white">
              {lightboxIndex + 1} / {gallery.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
